import React, { useEffect, useState } from "react";
import { Form, message } from "antd";
import { useQueryState } from "nuqs";
import { useMessageApi } from "../../../provider/AntdNotification";
import { usePageDetail, usePageSave } from "../../../hooks/api/page/hooks";
import TabsContain from "../../components/reuseComp/TabsContain";
import { getSingleImageUrl } from "../../../lib/utils";
import { IPageDetailPayload } from "@src/types/api/page";
import Banner from "../../components/sections/Banner";
import { SectionField } from "../home/HomeTabs";
import MemberTable from "./teamDetail";

const TeamTabs: React.FC<{
  setBreadcrumb: React.Dispatch<React.SetStateAction<string>>;
}> = ({ setBreadcrumb }) => {
  const message = useMessageApi();
  const [tab, setTab] = useQueryState("tab");
  const activeTab = tab || "Banner";
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  const [pageData, setPageData] = useState<any>(null);

  const { data } = usePageDetail("TEAM");
  const { mutate: updatePage, isPending } = usePageSave({});
  const [reviewMode, setReviewMode] = useState<"list" | "add" | "edit">("list");
  const [editingReviewIndex, setEditingReviewIndex] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (data?.data) setPageData(data.data);
  }, [data]);

  const handleChangeTab = (section: string) => {
    setTab(section);
    setIsEditing(false);
    setBreadcrumb(section);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const members = [...(pageData?.team?.member ?? [])];

      if (reviewMode === "add" && values?.team?.member?.length) {
        members.push(values.team.member[0]);
      }

      if (
        reviewMode === "edit" &&
        editingReviewIndex !== null &&
        values?.team?.member?.length
      ) {
        members[editingReviewIndex] = values.team.member[0];
      }

      const payload: IPageDetailPayload & { _id?: string } = {
        _id: pageData?._id,
        pageType: "TEAM",
        team: {
          banner: {
            heading:
              values.banner?.heading ?? pageData?.team?.banner?.heading ?? "",
            image: getSingleImageUrl(
              values.banner?.image ?? pageData?.team?.banner?.image,
            ),
          },
          member: members,
        },
      };

      updatePage(payload, {
        onSuccess: (res) => {
          message.success("Page updated successfully");
          setPageData(res.data);
          if (activeTab === "Banner") {
            setIsEditing(false);
          }
          setReviewMode("list");
          setEditingReviewIndex(null);
          form.resetFields();
        },
        onError: () => {
          message.error("Failed to update page");
        },
      });
    } catch (error) {
      console.log("❌ Validation failed:", error);
    }
  };

  const handleDelete = (index: number) => {
    if (!pageData) return;

    const updatedMembers = (pageData.team?.member ?? []).filter(
      (_: any, i: number) => i !== index,
    );

    const payload: IPageDetailPayload & { _id?: string } = {
      _id: pageData._id,
      pageType: "TEAM",
      team: {
        ...pageData.team,
        member: updatedMembers,
      },
    };

    updatePage(payload, {
      onSuccess: () => {
        message.success("Member deleted successfully");
        setPageData(payload);
        setReviewMode("list");
        setEditingReviewIndex(null);
      },
      onError: () => {
        message.error("Failed to delete member");
      },
    });
  };

  const sections = ["Banner", "Team Members"];

  return (
    <div>
      <TabsContain
        sections={sections}
        handleChangeTab={handleChangeTab}
        activeTab={activeTab}
      />

      {activeTab === "Banner" && (
        <Banner
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleCancel={handleCancel}
          handleSave={handleSave}
          form={form}
          bannerData={pageData?.team?.banner}
          loading={isPending}
        />
      )}
      {activeTab === "Team Members" && (
        <MemberTable
          memberData={pageData?.team?.member}
          form={form}
          handleSave={handleSave}
          handleCancel={handleCancel}
          loading={isPending}
          mode={reviewMode}
          editingIndex={editingReviewIndex}
          setMode={setReviewMode}
          onDelete={handleDelete}
          setEditingIndex={setEditingReviewIndex}
        />
      )}
    </div>
  );
};

export default TeamTabs;
